import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import TrustModuleNav from '../../components/trust/TrustModuleNav';

const TrustReports = () => {
    const [reconciliations, setReconciliations] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [toast, setToast] = useState(null);

    const showToast = (message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 4000);
    };

    const [formData, setFormData] = useState({
        account: '',
        reconciliation_date: new Date().toISOString().split('T')[0],
        period_start: '',
        period_end: '',
        bank_statement_balance: '',
        outstanding_deposits: '0.00',
        outstanding_withdrawals: '0.00',
        notes: ''
    });

    useEffect(() => {
        fetchReconciliations();
        fetchAccounts();
    }, []);

    const fetchReconciliations = async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch('/api/trust/reconciliations/', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            setReconciliations(Array.isArray(data) ? data : data.results || []);
        } catch (err) {
            setError(err.message);
            console.error('Error fetching reconciliations:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchAccounts = async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch('/api/trust/accounts/', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            setAccounts(Array.isArray(data) ? data : data.results || []);
        } catch (err) {
            console.error('Error fetching accounts:', err);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch('/api/trust/reconciliations/', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(formData)
            });

            if (!response.ok) throw new Error('Failed to create reconciliation');

            setShowModal(false);
            showToast('Reconciliation created successfully');
            fetchReconciliations();
        } catch (err) {
            showToast(err.message, 'error');
        }
    };

    const handleApprove = async (id) => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`/api/trust/reconciliations/${id}/approve/`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || 'Failed to approve');
            }

            showToast('Reconciliation approved successfully');
            fetchReconciliations();
        } catch (err) {
            showToast(err.message, 'error');
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount);
    };

    const getStatusBadge = (status, isReconciled) => {
        if (status === 'APPROVED') {
            return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">Approved</span>;
        }
        if (isReconciled) {
            return <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700">Reconciled</span>;
        }
        if (status === 'IN_PROGRESS') {
            return <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-700">In Progress</span>;
        }
        return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700">Draft</span>;
    };

    if (loading) {
        return (
            <div className="p-6 bg-gray-50 min-h-screen">
                <div className="mb-6 flex items-center justify-between">
                    <div>
                        <div className="h-4 w-40 bg-gray-200 rounded animate-pulse mb-3"></div>
                        <div className="h-7 w-56 bg-gray-200 rounded animate-pulse"></div>
                    </div>
                    <div className="h-10 w-40 bg-gray-200 rounded-lg animate-pulse"></div>
                </div>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                    {[1, 2, 3].map(i => (
                        <div key={i} className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className="h-4 w-32 bg-gray-200 rounded animate-pulse"></div>
                                <div className="h-4 w-20 bg-gray-100 rounded animate-pulse"></div>
                            </div>
                            <div className="h-6 w-16 bg-gray-100 rounded-full animate-pulse"></div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (error && reconciliations.length === 0) {
        return (
            <div className="p-6 bg-gray-50 min-h-screen">
                <div className="mb-6">
                    <nav className="text-sm text-gray-500 mb-2">
                        <Link to="/trust" className="hover:text-blue-600">Trust Accounts</Link>
                        <span className="mx-2">›</span>
                        <span>Reconciliation</span>
                    </nav>
                    <h1 className="text-2xl font-bold text-gray-900">Reconciliation & Reports</h1>
                </div>
                <div className="max-w-md mx-auto mt-12">
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 text-center">
                        <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                            </svg>
                        </div>
                        <h2 className="text-xl font-semibold text-gray-900 mb-2">Unable to Load Reports</h2>
                        <p className="text-gray-500 mb-6">We couldn't connect to the server. Please try again.</p>
                        <button
                            onClick={() => { setLoading(true); setError(null); fetchReconciliations(); }}
                            className="inline-flex items-center px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                        >
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            Try Again
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            {/* Toast */}
            {toast && (
                <div className={`fixed top-4 right-4 z-50 flex items-center gap-3 p-4 rounded-lg border shadow-lg max-w-sm transition-all ${toast.type === 'error' ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                    {toast.type === 'error' ? (
                        <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    ) : (
                        <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    )}
                    <p className="text-sm text-gray-700">{toast.message}</p>
                </div>
            )}

            {/* Header */}
            <div className="mb-4">
                <nav className="text-sm text-gray-500 mb-2">
                    <Link to="/trust" className="hover:text-blue-600">Trust Accounts</Link>
                    <span className="mx-2">›</span>
                    <span>Reconciliations</span>
                </nav>
            </div>
            <div className="mb-4 flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Trust Account Reconciliations</h1>
                    <p className="text-gray-600 mt-1">Monthly three-way reconciliation for compliance</p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => window.print()}
                        aria-label="Print reconciliation report"
                        className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center gap-2 font-medium transition-colors"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                        </svg>
                        Print
                    </button>
                    <button
                        onClick={() => setShowModal(true)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 font-medium transition-colors"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        New Reconciliation
                    </button>
                </div>
            </div>

            {/* Trust module sub-navigation */}
            <TrustModuleNav />

            {/* Info Card */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6" role="note">
                <div className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                        <h3 className="font-medium text-blue-800 mb-1">Three-Way Reconciliation</h3>
                        <p className="text-sm text-blue-700">
                            Ensures: <strong>(1)</strong> Bank Statement Balance = <strong>(2)</strong> Book Balance = <strong>(3)</strong> Total Client Ledgers.
                            Required monthly for compliance with Law Society regulations.
                        </p>
                    </div>
                </div>
            </div>

            {/* Reconciliation summary stats */}
            {reconciliations.length > 0 && (() => {
                const approved = reconciliations.filter(r => r.status === 'APPROVED').length;
                const pending  = reconciliations.filter(r => r.status !== 'APPROVED').length;
                const unreconciled = reconciliations.filter(r => !r.is_reconciled).length;
                return (
                    <div className="grid grid-cols-3 gap-4 mb-5">
                        <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3">
                            <p className="text-xs text-green-700 font-medium">Approved</p>
                            <p className="text-2xl font-bold text-green-800">{approved}</p>
                        </div>
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-3">
                            <p className="text-xs text-yellow-700 font-medium">Pending Approval</p>
                            <p className="text-2xl font-bold text-yellow-800">{pending}</p>
                        </div>
                        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                            <p className="text-xs text-red-700 font-medium">Has Variance</p>
                            <p className="text-2xl font-bold text-red-800">{unreconciled}</p>
                        </div>
                    </div>
                );
            })()}

            {/* Reconciliations Table */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Period</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Bank Balance</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Book Balance</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Client Ledgers</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Variance</th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {reconciliations.length > 0 ? (
                            reconciliations.map((rec) => {
                                const hasVariance = parseFloat(rec.variance || 0) !== 0;
                                return (
                                <tr key={rec.id} className={`hover:bg-gray-50 ${hasVariance ? 'bg-red-50' : ''}`}>
                                    <td className="px-6 py-4">
                                        <p className="font-medium text-gray-900">{rec.account_name}</p>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-600">
                                        {rec.period_start} – {rec.period_end}
                                    </td>
                                    <td className="px-6 py-4 text-right text-sm text-gray-900 font-mono">
                                        {formatCurrency(rec.bank_statement_balance)}
                                    </td>
                                    <td className="px-6 py-4 text-right text-sm text-gray-900 font-mono">
                                        {formatCurrency(rec.book_balance)}
                                    </td>
                                    <td className="px-6 py-4 text-right text-sm text-gray-900 font-mono">
                                        {formatCurrency(rec.client_ledger_total)}
                                    </td>
                                    <td className={`px-6 py-4 text-right text-sm font-medium font-mono ${hasVariance ? 'text-red-600' : 'text-green-600'}`}>
                                        {hasVariance && <span className="mr-1" aria-label="Variance alert">⚠</span>}
                                        {formatCurrency(rec.variance || 0)}
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        {getStatusBadge(rec.status, rec.is_reconciled)}
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        {rec.status !== 'APPROVED' && rec.is_reconciled && (
                                            <button
                                                onClick={() => handleApprove(rec.id)}
                                                className="text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors px-2 py-1 rounded hover:bg-blue-50"
                                            >
                                                Approve
                                            </button>
                                        )}
                                    </td>
                                </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                                    No reconciliations found. Create your first monthly reconciliation.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* New Reconciliation Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                                <h2 className="text-xl font-semibold text-gray-900">New Reconciliation</h2>
                                <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Trust Account *</label>
                                    <select
                                        required
                                        value={formData.account}
                                        onChange={(e) => setFormData({ ...formData, account: e.target.value })}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                    >
                                        <option value="">Select Account</option>
                                        {accounts.map(acc => (
                                            <option key={acc.id} value={acc.id}>{acc.account_name}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Reconciliation Date *</label>
                                    <input
                                        type="date"
                                        required
                                        value={formData.reconciliation_date}
                                        onChange={(e) => setFormData({ ...formData, reconciliation_date: e.target.value })}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Period Start *</label>
                                    <input
                                        type="date"
                                        required
                                        value={formData.period_start}
                                        onChange={(e) => setFormData({ ...formData, period_start: e.target.value })}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Period End *</label>
                                    <input
                                        type="date"
                                        required
                                        value={formData.period_end}
                                        onChange={(e) => setFormData({ ...formData, period_end: e.target.value })}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                    />
                                </div>
                            </div>

                            <div className="bg-gray-50 rounded-lg p-4">
                                <h4 className="font-medium text-gray-900 mb-3">Bank Statement Figures</h4>
                                <div className="grid grid-cols-3 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Statement Balance *</label>
                                        <div className="relative">
                                            <span className="absolute left-3 top-2 text-gray-500">$</span>
                                            <input
                                                type="number"
                                                step="0.01"
                                                required
                                                value={formData.bank_statement_balance}
                                                onChange={(e) => setFormData({ ...formData, bank_statement_balance: e.target.value })}
                                                className="w-full border border-gray-300 rounded-lg pl-8 pr-3 py-2"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Outstanding Deposits</label>
                                        <div className="relative">
                                            <span className="absolute left-3 top-2 text-gray-500">$</span>
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={formData.outstanding_deposits}
                                                onChange={(e) => setFormData({ ...formData, outstanding_deposits: e.target.value })}
                                                className="w-full border border-gray-300 rounded-lg pl-8 pr-3 py-2"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Outstanding Withdrawals</label>
                                        <div className="relative">
                                            <span className="absolute left-3 top-2 text-gray-500">$</span>
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={formData.outstanding_withdrawals}
                                                onChange={(e) => setFormData({ ...formData, outstanding_withdrawals: e.target.value })}
                                                className="w-full border border-gray-300 rounded-lg pl-8 pr-3 py-2"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                                <textarea
                                    value={formData.notes}
                                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                    rows="3"
                                    placeholder="Any notes about this reconciliation"
                                ></textarea>
                            </div>

                            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                >
                                    Create Reconciliation
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TrustReports;
